import cn from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button, IconClose, Modal, Typography } from '@grammarly/badger-ui'
import styles from './wall.module.scss'
import { AlreadyHaveAnAccountLink, SignUpButton } from './ctas'
import { useEffect } from 'react'
import { gnarSpec } from '../../../../../lib/tracking/gnar'
import { usePageInfo } from '../../../../../contexts/page-context'
import { SignUpModalContent } from '../../../../ui-lib/modal/signup'
import { useExperimentService, useUserService } from '../../../../../lib/hooks'

interface WallProps {
  appElementForModal: string
  show: boolean
  close: () => void
  parentSelector: string
  isForPlagiarismPlanPage: boolean
  slug?: string
  isMobileOrTablet: boolean
}

export const Wall = (props: WallProps) => {
  const intl = useIntl()
  const { appElementForModal, show, close, parentSelector, isForPlagiarismPlanPage } = props
  const { slug, pageId } = usePageInfo()
  const { isGoogleCtaSignupWallV2 } = useExperimentService()
  const { isAnonymous } = useUserService().currentUser

  const checklistItems = [
    intl.formatMessage({
      id: 'textChecker.reportFlow.form.wall.item1',
      description: 'Text Checker - Report Flow - Form - Wall - Item 1',
      defaultMessage: 'Find out where plagiarism appears in text',
    }),
    intl.formatMessage({
      id: 'textChecker.reportFlow.form.wall.item2',
      description: 'Text Checker - Report Flow - Form - Wall - Item 2',
      defaultMessage: 'Instantly cite your sources',
    }),
    intl.formatMessage({
      id: 'textChecker.reportFlow.form.wall.item3',
      description: 'Text Checker - Report Flow - Form - Wall - Item 3',
      defaultMessage: 'Ensure mistake-free writing',
    }),
  ]

  useEffect(() => {
    if (show) {
      gnarSpec.plagiarismWallShow(pageId, slug)
    }
  }, [show, pageId, slug])

  const titleId = `signup-dialog-title`
  const isGoogleCtaSignupWallV2Enabled = show && isGoogleCtaSignupWallV2(isAnonymous)

  return (
    <Modal
      isOpen={show}
      onRequestClose={close}
      aria={{ labelledby: titleId }}
      appElementForModal={appElementForModal}
      className={cn(styles.wallModal, styles.defaultWallModal)}
      overlayClassName={styles.modalOverlay}
      parentSelector={() => document.querySelector(parentSelector) || document.body}
    >
      <div>
        <Button
          className={styles.closeButton}
          padding="compact"
          onClick={close}
          aria-label={intl.formatMessage({
            id: 'global.uiAction.close',
            description: 'Text for CTA that closes an element',
            defaultMessage: 'Close',
          })}
        >
          <IconClose aria-hidden="true" />
        </Button>
        {isGoogleCtaSignupWallV2Enabled ? (
          <SignUpModalContent />
        ) : (
          <>
            <Typography.TM as="h2" id={titleId}>
              <strong>
                <FormattedMessage
                  id="textChecker.reportFlow.form.wall.heading"
                  description="Text Checker - Report Flow - Form - Wall - Heading"
                  defaultMessage="Sign up for unlimited plagiarism and AI detection"
                />
              </strong>
            </Typography.TM>
            <ul className={styles.checklist}>
              {checklistItems.map((item, index) => (
                <Typography.TS key={index} as="li">
                  {item}
                </Typography.TS>
              ))}
            </ul>
            <SignUpButton
              className={styles.cta}
              placement="signupWall"
              isForPlagiarismPlanPage={isForPlagiarismPlanPage}
            />
            <AlreadyHaveAnAccountLink placement="signupWall" className={styles.loginSupplement} />
          </>
        )}
      </div>
    </Modal>
  )
}
